import React, { useState, useEffect, useCallback } from 'react'
import { Container, Button, Form, Modal, Table, Tabs, Tab, Badge } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import V2Layout from '../components/V2Layout'
import './Admin.css'
import axios from 'axios'

const API = ''

const DEFAULT_DATA = {
    title: 'Precision Fluid Controls, Inc.',
    tagline: '',
    yearStart: 2004,
    yearEnd: 2026,
    stats: [],
    lanes: [],
    facilities: [],
    equipment: [],
    eras: [],
    milestones: [],
    legend: [],
}

const PRESET_COLORS = [
    '#AFA9EC', '#7F77DD', '#534AB7', '#6B65B0', '#9590D0',
    '#5DCAA5', '#3C7D6A', '#1D9E75',
    '#D4537E', '#D85A30', '#BA7517',
    '#185FA5', '#26215C',
]

const ColorPicker = ({ value, onChange }) => (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
        {PRESET_COLORS.map(c => (
            <button
                key={c}
                type="button"
                onClick={() => onChange(c)}
                style={{
                    width: 22, height: 22, borderRadius: 4, border: value === c ? '2px solid #333' : '1px solid #ccc',
                    background: c, cursor: 'pointer', padding: 0,
                }}
                title={c}
            />
        ))}
        <input
            type="color"
            value={value || '#7F77DD'}
            onChange={e => onChange(e.target.value)}
            style={{ width: 28, height: 22, border: 'none', padding: 0, cursor: 'pointer' }}
        />
    </div>
)

const TimelineAdminScreen = () => {
    const [data, setData] = useState(null)
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [activeTab, setActiveTab] = useState('general')

    // Modal state for lanes
    const [showLaneModal, setShowLaneModal] = useState(false)
    const [editLaneIdx, setEditLaneIdx] = useState(null)
    const [laneForm, setLaneForm] = useState([])

    const load = useCallback(() => {
        axios.get(`${API}/api/timeline`)
            .then(r => setData(r.data || DEFAULT_DATA))
            .catch(() => setData(DEFAULT_DATA))
    }, [])

    useEffect(() => { load() }, [load])

    const save = async () => {
        setSaving(true)
        try {
            await axios.put(`${API}/api/timeline`, data)
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
        } catch (e) { alert('Save failed: ' + e.message) }
        setSaving(false)
    }

    if (!data) return <V2Layout><div style={{ minHeight: '60vh' }} /></V2Layout>

    const updateField = (field, value) => setData(prev => ({ ...prev, [field]: value }))

    // Stats helpers
    const addStat = () => updateField('stats', [...data.stats, { label: '', value: '' }])
    const updateStat = (i, key, val) => {
        const arr = [...data.stats]
        arr[i] = { ...arr[i], [key]: val }
        updateField('stats', arr)
    }
    const removeStat = (i) => updateField('stats', data.stats.filter((_, idx) => idx !== i))

    // Facility helpers
    const addFacility = () => updateField('facilities', [...data.facilities, { start: data.yearEnd - 1, end: data.yearEnd, sqft: 1000, location: '' }])
    const updateFacility = (i, key, val) => {
        const arr = [...data.facilities]
        arr[i] = { ...arr[i], [key]: key === 'location' ? val : +val }
        updateField('facilities', arr)
    }
    const removeFacility = (i) => updateField('facilities', data.facilities.filter((_, idx) => idx !== i))

    // Equipment helpers
    const addEquipment = () => updateField('equipment', [...data.equipment, { start: data.yearEnd - 1, end: data.yearEnd, count: 1 }])
    const updateEquipment = (i, key, val) => {
        const arr = [...data.equipment]
        arr[i] = { ...arr[i], [key]: +val }
        updateField('equipment', arr)
    }
    const removeEquipment = (i) => updateField('equipment', data.equipment.filter((_, idx) => idx !== i))

    // Era helpers
    const addEra = () => updateField('eras', [...data.eras, { start: data.yearEnd - 2, end: data.yearEnd, color: 'rgba(127,119,221,0.07)', label: '' }])
    const updateEra = (i, key, val) => {
        const arr = [...data.eras]
        arr[i] = { ...arr[i], [key]: (key === 'start' || key === 'end') ? +val : val }
        updateField('eras', arr)
    }
    const removeEra = (i) => updateField('eras', data.eras.filter((_, idx) => idx !== i))

    // Milestone helpers
    const addMilestone = () => updateField('milestones', [...data.milestones, { year: data.yearEnd - 1, labels: [''], align: 0 }])
    const updateMilestone = (i, key, val) => {
        const arr = [...data.milestones]
        if (key === 'labels') arr[i] = { ...arr[i], labels: val.split('\n') }
        else arr[i] = { ...arr[i], [key]: +val }
        updateField('milestones', arr)
    }
    const removeMilestone = (i) => updateField('milestones', data.milestones.filter((_, idx) => idx !== i))

    // Legend helpers
    const addLegend = () => updateField('legend', [...data.legend, { color: '#7F77DD', label: '' }])
    const updateLegend = (i, key, val) => {
        const arr = [...data.legend]
        arr[i] = { ...arr[i], [key]: val }
        updateField('legend', arr)
    }
    const removeLegend = (i) => updateField('legend', data.legend.filter((_, idx) => idx !== i))

    // Lane helpers
    const openLaneEditor = (idx) => {
        setEditLaneIdx(idx)
        setLaneForm(idx !== null ? JSON.parse(JSON.stringify(data.lanes[idx])) : [{ label: '', start: data.yearStart, end: data.yearEnd, color: '#7F77DD' }])
        setShowLaneModal(true)
    }
    const saveLane = () => {
        const lanes = [...data.lanes]
        if (editLaneIdx !== null) lanes[editLaneIdx] = laneForm
        else lanes.push(laneForm)
        updateField('lanes', lanes)
        setShowLaneModal(false)
    }
    const removeLane = (i) => updateField('lanes', data.lanes.filter((_, idx) => idx !== i))
    const moveLane = (i, dir) => {
        const lanes = [...data.lanes]
        const j = i + dir
        if (j < 0 || j >= lanes.length) return
            ;[lanes[i], lanes[j]] = [lanes[j], lanes[i]]
        updateField('lanes', lanes)
    }

    return (
        <V2Layout pageClass="pfc-admin">
            <style>{`
                .pfc-admin-container {
                    padding: 3rem 0 5rem;
                    background-color: #faf9f6;
                    min-height: 80vh;
                }
                .pfc-admin-card {
                    background: #fff;
                    border: 1px solid #e5e3dd;
                    border-radius: 8px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
                    padding: 32px;
                    margin-bottom: 24px;
                }
                .pfc-admin-tabs.nav-pills {
                    border-bottom: 1px solid #e5e3dd;
                    padding-bottom: 12px;
                    margin-bottom: 24px;
                }
                .pfc-admin-tabs.nav-pills .nav-link {
                    font-family: var(--pfc-mono, monospace);
                    font-size: 13px;
                    font-weight: 600;
                    color: #5F5E5A;
                    background: none;
                    border: 1px solid transparent;
                    padding: 8px 16px;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .pfc-admin-tabs.nav-pills .nav-link:hover {
                    color: #26215C;
                    background-color: rgba(38, 33, 148, 0.04);
                }
                .pfc-admin-tabs.nav-pills .nav-link.active {
                    color: #fff !important;
                    background-color: #26215C !important;
                    box-shadow: 0 4px 12px rgba(38, 33, 92, 0.2);
                }
                .pfc-admin-table {
                    border-collapse: separate;
                    border-spacing: 0;
                    border: 1px solid #e5e3dd;
                    border-radius: 6px;
                    overflow: hidden;
                    background-color: #fff;
                    margin-top: 1rem;
                }
                .pfc-admin-table thead th {
                    background-color: #26215C !important;
                    color: #fff !important;
                    font-family: var(--pfc-mono, monospace);
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    font-weight: 600;
                    border: none;
                    padding: 12px 16px;
                }
                .pfc-admin-table tbody td {
                    padding: 12px 16px;
                    vertical-align: middle;
                    font-size: 14px;
                    color: #333;
                    border-bottom: 1px solid #f1efe8;
                }
                .pfc-admin-table tbody tr:last-child td {
                    border-bottom: none;
                }
                .pfc-admin-table tbody tr {
                    transition: background-color 0.15s ease;
                }
                .pfc-admin-table tbody tr:hover {
                    background-color: rgba(38, 33, 148, 0.01) !important;
                }
                .btn-pfc-primary {
                    background-color: #26215C !important;
                    border-color: #26215C !important;
                    color: #fff !important;
                    font-family: var(--pfc-mono, monospace);
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    padding: 8px 16px;
                    border-radius: 4px;
                    font-weight: 600;
                    transition: all 0.2s ease;
                }
                .btn-pfc-primary:hover, .btn-pfc-primary:focus {
                    background-color: #1a1645 !important;
                    border-color: #1a1645 !important;
                    color: #fff !important;
                    box-shadow: 0 4px 12px rgba(38, 33, 92, 0.15);
                }
                .btn-pfc-secondary {
                    background-color: #fff !important;
                    border-color: #ced4da !important;
                    color: #5c5a54 !important;
                    font-family: var(--pfc-mono, monospace);
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    padding: 8px 16px;
                    border-radius: 4px;
                    font-weight: 600;
                    transition: all 0.2s ease;
                }
                .btn-pfc-secondary:hover {
                    background-color: #faf9f6 !important;
                    border-color: #adb5bd !important;
                    color: #333 !important;
                }
            `}</style>

            <section className="pfc-admin-container">
                <Container>
                    <div className="pfc-admin-card">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                            <div>
                                <h1 style={{ color: '#26215C', fontSize: '2rem', fontWeight: 700, margin: 0 }}>Timeline Admin</h1>
                                <p className="text-muted mb-0 mt-1" style={{ fontSize: '13px' }}>
                                    Update the company growth history lanes, add historical milestones, and edit equipment and facility footprint stats.
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                {saved && <Badge bg="success" style={{ fontSize: '0.8rem' }}>Saved</Badge>}
                                <Button
                                    className="btn-pfc-primary"
                                    onClick={save}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                                <a href="/story" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: '#4A3FC7', fontWeight: 600 }}>
                                    Preview →
                                </a>
                            </div>
                        </div>

                        {/* Top sub-navigation bar */}
                        <div className="d-flex gap-3 mb-4 align-items-center pfc-admin-subnav">
                            <Link to="/admin" className="pfc-admin-subnav-link">Dashboard</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/products/admin" className="pfc-admin-subnav-link">Products Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/capabilities/admin" className="pfc-admin-subnav-link">Capabilities Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/news/admin" className="pfc-admin-subnav-link">News Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/story/admin" className="pfc-admin-subnav-link active">Timeline Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/certifications/admin" className="pfc-admin-subnav-link">Certifications Admin</Link>
                        </div>

                        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3 pfc-admin-tabs" variant="pills">
                            {/* ── General ── */}
                            <Tab eventKey="general" title="General">
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontWeight: 600 }}>Page Title</Form.Label>
                                    <Form.Control value={data.title} onChange={e => updateField('title', e.target.value)} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontWeight: 600 }}>Tagline</Form.Label>
                                    <Form.Control as="textarea" rows={3} value={data.tagline} onChange={e => updateField('tagline', e.target.value)} />
                                </Form.Group>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <Form.Group className="mb-3" style={{ flex: 1 }}>
                                        <Form.Label style={{ fontWeight: 600 }}>Year Start</Form.Label>
                                        <Form.Control type="number" value={data.yearStart} onChange={e => updateField('yearStart', +e.target.value)} />
                                    </Form.Group>
                                    <Form.Group className="mb-3" style={{ flex: 1 }}>
                                        <Form.Label style={{ fontWeight: 600 }}>Year End</Form.Label>
                                        <Form.Control type="number" value={data.yearEnd} onChange={e => updateField('yearEnd', +e.target.value)} />
                                    </Form.Group>
                                </div>
                            </Tab>

                            {/* ── Stats ── */}
                            <Tab eventKey="stats" title="Stats Bar">
                                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>The stats shown below the page title.</p>
                                <Table bordered size="sm" className="pfc-admin-table">
                                    <thead><tr><th>Value</th><th>Label</th><th style={{ width: 60 }}></th></tr></thead>
                                    <tbody>
                                        {data.stats.map((s, i) => (
                                            <tr key={i}>
                                                <td><Form.Control size="sm" value={s.value} onChange={e => updateStat(i, 'value', e.target.value)} /></td>
                                                <td><Form.Control size="sm" value={s.label} onChange={e => updateStat(i, 'label', e.target.value)} /></td>
                                                <td><Button size="sm" variant="outline-danger" onClick={() => removeStat(i)}>×</Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Button size="sm" variant="outline-secondary" onClick={addStat}>+ Add Stat</Button>
                            </Tab>

                            {/* ── Product Lanes ── */}
                            <Tab eventKey="lanes" title="Product Lanes">
                                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                                    Each lane is a horizontal row. A lane can have multiple segments (e.g. a product that changed name).
                                </p>
                                <Table bordered size="sm" className="pfc-admin-table">
                                    <thead><tr><th>#</th><th>Segments</th><th style={{ width: 180 }}>Actions</th></tr></thead>
                                    <tbody>
                                        {data.lanes.map((lane, i) => (
                                            <tr key={i}>
                                                <td>{i + 1}</td>
                                                <td style={{ fontSize: '0.8rem' }}>
                                                    {lane.map(seg => `${seg.label} (${seg.start}–${seg.end})`).join(' → ')}
                                                </td>
                                                <td>
                                                    <Button size="sm" variant="outline-primary" onClick={() => openLaneEditor(i)} style={{ marginRight: 4 }}>Edit</Button>
                                                    <Button size="sm" variant="outline-secondary" onClick={() => moveLane(i, -1)} style={{ marginRight: 4 }}>↑</Button>
                                                    <Button size="sm" variant="outline-secondary" onClick={() => moveLane(i, 1)} style={{ marginRight: 4 }}>↓</Button>
                                                    <Button size="sm" variant="outline-danger" onClick={() => removeLane(i)}>×</Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Button size="sm" variant="outline-secondary" onClick={() => openLaneEditor(null)}>+ Add Lane</Button>
                            </Tab>

                            {/* ── Facilities ── */}
                            <Tab eventKey="facilities" title="Facilities">
                                <Table bordered size="sm" className="pfc-admin-table">
                                    <thead><tr><th>Location</th><th>Sq Ft</th><th>Start</th><th>End</th><th style={{ width: 60 }}></th></tr></thead>
                                    <tbody>
                                        {data.facilities.map((f, i) => (
                                            <tr key={i}>
                                                <td><Form.Control size="sm" value={f.location} onChange={e => updateFacility(i, 'location', e.target.value)} /></td>
                                                <td><Form.Control size="sm" type="number" value={f.sqft} onChange={e => updateFacility(i, 'sqft', e.target.value)} /></td>
                                                <td><Form.Control size="sm" type="number" value={f.start} onChange={e => updateFacility(i, 'start', e.target.value)} /></td>
                                                <td><Form.Control size="sm" type="number" value={f.end} onChange={e => updateFacility(i, 'end', e.target.value)} /></td>
                                                <td><Button size="sm" variant="outline-danger" onClick={() => removeFacility(i)}>×</Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Button size="sm" variant="outline-secondary" onClick={addFacility}>+ Add Facility</Button>
                            </Tab>

                            {/* ── Equipment ── */}
                            <Tab eventKey="equipment" title="CNC Fleet">
                                <Table bordered size="sm" className="pfc-admin-table">
                                    <thead><tr><th>Machine Count</th><th>Start</th><th>End</th><th style={{ width: 60 }}></th></tr></thead>
                                    <tbody>
                                        {data.equipment.map((eq, i) => (
                                            <tr key={i}>
                                                <td><Form.Control size="sm" type="number" value={eq.count} onChange={e => updateEquipment(i, 'count', e.target.value)} /></td>
                                                <td><Form.Control size="sm" type="number" value={eq.start} onChange={e => updateEquipment(i, 'start', e.target.value)} /></td>
                                                <td><Form.Control size="sm" type="number" value={eq.end} onChange={e => updateEquipment(i, 'end', e.target.value)} /></td>
                                                <td><Button size="sm" variant="outline-danger" onClick={() => removeEquipment(i)}>×</Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Button size="sm" variant="outline-secondary" onClick={addEquipment}>+ Add Period</Button>
                            </Tab>

                            {/* ── Eras ── */}
                            <Tab eventKey="eras" title="Eras">
                                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>Background shading and labels for time periods.</p>
                                <Table bordered size="sm" className="pfc-admin-table">
                                    <thead><tr><th>Label</th><th>Start</th><th>End</th><th>Color (rgba)</th><th style={{ width: 60 }}></th></tr></thead>
                                    <tbody>
                                        {data.eras.map((e, i) => (
                                            <tr key={i}>
                                                <td><Form.Control size="sm" value={e.label} onChange={ev => updateEra(i, 'label', ev.target.value)} /></td>
                                                <td><Form.Control size="sm" type="number" value={e.start} onChange={ev => updateEra(i, 'start', ev.target.value)} /></td>
                                                <td><Form.Control size="sm" type="number" value={e.end} onChange={ev => updateEra(i, 'end', ev.target.value)} /></td>
                                                <td><Form.Control size="sm" value={e.color} onChange={ev => updateEra(i, 'color', ev.target.value)} /></td>
                                                <td><Button size="sm" variant="outline-danger" onClick={() => removeEra(i)}>×</Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Button size="sm" variant="outline-secondary" onClick={addEra}>+ Add Era</Button>
                            </Tab>

                            {/* ── Milestones ── */}
                            <Tab eventKey="milestones" title="Milestones">
                                <Table bordered size="sm" className="pfc-admin-table">
                                    <thead><tr><th>Year</th><th>Labels (one per line)</th><th>Align</th><th style={{ width: 60 }}></th></tr></thead>
                                    <tbody>
                                        {data.milestones.map((m, i) => (
                                            <tr key={i}>
                                                <td><Form.Control size="sm" type="number" value={m.year} onChange={e => updateMilestone(i, 'year', e.target.value)} /></td>
                                                <td><Form.Control size="sm" as="textarea" rows={2} value={m.labels.join('\n')} onChange={e => updateMilestone(i, 'labels', e.target.value)} /></td>
                                                <td>
                                                    <Form.Select size="sm" value={m.align} onChange={e => updateMilestone(i, 'align', e.target.value)}>
                                                        <option value={0}>Top</option>
                                                        <option value={1}>Bottom</option>
                                                    </Form.Select>
                                                </td>
                                                <td><Button size="sm" variant="outline-danger" onClick={() => removeMilestone(i)}>×</Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Button size="sm" variant="outline-secondary" onClick={addMilestone}>+ Add Milestone</Button>
                            </Tab>

                            {/* ── Legend ── */}
                            <Tab eventKey="legend" title="Legend">
                                <Table bordered size="sm" className="pfc-admin-table">
                                    <thead><tr><th style={{ width: 160 }}>Color</th><th>Label</th><th style={{ width: 60 }}></th></tr></thead>
                                    <tbody>
                                        {data.legend.map((l, i) => (
                                            <tr key={i}>
                                                <td><ColorPicker value={l.color} onChange={c => updateLegend(i, 'color', c)} /></td>
                                                <td><Form.Control size="sm" value={l.label} onChange={e => updateLegend(i, 'label', e.target.value)} /></td>
                                                <td><Button size="sm" variant="outline-danger" onClick={() => removeLegend(i)}>×</Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Button size="sm" variant="outline-secondary" onClick={addLegend}>+ Add Legend Item</Button>
                            </Tab>
                        </Tabs>
                    </div>

                    {/* Lane Editor Modal */}
                    <Modal show={showLaneModal} onHide={() => setShowLaneModal(false)} centered size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title style={{ fontSize: '1.1rem' }}>{editLaneIdx !== null ? 'Edit Lane' : 'New Lane'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                                A lane can have multiple segments. Each segment is a colored bar on the timeline.
                            </p>
                            {laneForm.map((seg, i) => (
                                <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: '0.75rem', marginBottom: '0.75rem' }}>
                                    <Form.Group className="mb-2">
                                        <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Label</Form.Label>
                                        <Form.Control size="sm" value={seg.label} onChange={e => {
                                            const arr = [...laneForm]; arr[i] = { ...arr[i], label: e.target.value }; setLaneForm(arr)
                                        }} />
                                    </Form.Group>
                                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                                        <Form.Group style={{ flex: 1 }}>
                                            <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Start</Form.Label>
                                            <Form.Control size="sm" type="number" value={seg.start} onChange={e => {
                                                const arr = [...laneForm]; arr[i] = { ...arr[i], start: +e.target.value }; setLaneForm(arr)
                                            }} />
                                        </Form.Group>
                                        <Form.Group style={{ flex: 1 }}>
                                            <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem' }}>End</Form.Label>
                                            <Form.Control size="sm" type="number" value={seg.end} onChange={e => {
                                                const arr = [...laneForm]; arr[i] = { ...arr[i], end: +e.target.value }; setLaneForm(arr)
                                            }} />
                                        </Form.Group>
                                    </div>
                                    <Form.Group className="mt-2">
                                        <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Color</Form.Label>
                                        <ColorPicker value={seg.color} onChange={c => {
                                            const arr = [...laneForm]; arr[i] = { ...arr[i], color: c }; setLaneForm(arr)
                                        }} />
                                    </Form.Group>
                                    {laneForm.length > 1 && (
                                        <Button size="sm" variant="outline-danger" className="mt-2" onClick={() => setLaneForm(laneForm.filter((_, idx) => idx !== i))}>
                                            Remove Segment
                                        </Button>
                                    )}
                                </div>
                            ))}
                            <Button size="sm" variant="outline-secondary" onClick={() => setLaneForm([...laneForm, { label: '', start: data.yearStart, end: data.yearEnd, color: '#7F77DD' }])}>
                                + Add Segment
                            </Button>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button className="btn-pfc-secondary" onClick={() => setShowLaneModal(false)}>Cancel</Button>
                            <Button className="btn-pfc-primary" onClick={saveLane}>Save Lane</Button>
                        </Modal.Footer>
                    </Modal>
                </Container>
            </section>
        </V2Layout>
    )
}

export default TimelineAdminScreen
